import { Button, Flex, HStack, Input, Modal, Select } from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { companyService } from 'services/companyService';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteUserModal = ({ isOpen, onClose, onSuccess }: InviteUserModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [role, setRole] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const selectedRole = role[0];

    // Validation
    if (!email || !selectedRole) {
      setError(t('app.users.inviteModal.allFieldsRequired'));
      return;
    }

    setLoading(true);
    try {
      await companyService.inviteUser({
        email,
        role: selectedRole,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || t('app.users.inviteModal.inviteFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: 'var(--modal-header-text)'
          }}>
            {t('app.users.inviteModal.title')}
          </h3>
          <Modal.CloseTrigger asChild>
            <button style={{
              borderRadius: '0.5rem',
              padding: '0.25rem',
              transition: 'background-color 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <MdClose style={{
                height: '1.5rem',
                width: '1.5rem',
                color: 'var(--modal-close-icon)'
              }} />
            </button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div style={{
            marginBottom: '1rem',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            fontSize: '0.875rem',
            backgroundColor: 'var(--alert-error-bg)',
            color: 'var(--alert-error-text)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} id="invite-user-form">
          <Flex direction="column" gap={3}>
            <Input
              label={t('app.users.inviteModal.emailLabel')}
              placeholder={t('app.users.inviteModal.emailPlaceholder')}
              id="email"
              name="email"
              type="email"
            />

            <Select
              label={t('app.users.inviteModal.roleLabel')}
              placeholder={t('app.users.inviteModal.selectRole')}
              items={[
                { label: t('app.users.inviteModal.userRole'), value: 'User' },
                { label: t('app.users.inviteModal.adminRole'), value: 'Admin' },
              ]}
              value={role}
              onValueChange={(details) => setRole(details.value)}
              required
            />
          </Flex>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <HStack gap={3}>
          <Button
            type="button"
            onClick={onClose}
            variant="subtle"
            colorPalette="gray"
            flex={1}
          >
            {t('app.common.cancel')}
          </Button>
          <Button
            type="submit"
            form="invite-user-form"
            disabled={loading}
            variant="solid"
            colorPalette="blue"
            flex={1}
          >
            {loading ? t('app.users.inviteModal.inviting') : t('app.users.inviteModal.inviteButton')}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default InviteUserModal;
